import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useSubscription } from './hooks/useSubscription';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { StartTrialPage } from './pages/StartTrialPage';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { AlertsPage } from './pages/AlertsPage';
import { UsersPage } from './pages/UsersPage';
import { PeoplePage } from './pages/PeoplePage';
import { CompliancePage } from './pages/CompliancePage';
import { NetworkPage } from './pages/NetworkPage';
import { NetworkActivityPage } from './pages/NetworkActivityPage';
import { HealthPage } from './pages/HealthPage';
import { SettingsPage } from './pages/SettingsPage';
import ThreatDetectionPage from './pages/ThreatDetectionPage';
import AutoFixPage from './pages/AutoFixPage';
import AdminPage from './pages/AdminPage';
import { SetupWizardPage } from './pages/SetupWizardPage';
import { MSPLayout } from './components/MSPLayout';
import { MSPDashboardPage } from './pages/msp/MSPDashboardPage';
import { MSPClientsPage } from './pages/msp/MSPClientsPage';
import { MSPClientDetailPage } from './pages/msp/MSPClientDetailPage';
import { MSPSettingsPage } from './pages/msp/MSPSettingsPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import './App.css';

const PrivateRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/login" />;
};

/** Forces users with must_change_password to the password change page. Use inside PrivateRoute. */
const RequirePasswordChanged = ({ children }) => {
    const { user } = useAuth();
    if (user?.must_change_password) {
        return <Navigate to="/change-password" replace />;
    }
    return children;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, isClient } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to={isClient ? "/dashboard" : "/start-trial"} replace />;
    }
    return children;
};

/** Redirects to /start-trial if user has no plan, /setup if onboarding incomplete, else /dashboard. Use inside PrivateRoute. */
const DefaultRedirect = () => {
    const { user } = useAuth();
    const { subscription, loading } = useSubscription();
    const [onboardingChecked, setOnboardingChecked] = React.useState(false);
    const [needsSetup, setNeedsSetup] = React.useState(false);

    // Client users skip subscription/onboarding checks — go straight to dashboard
    const isClient = user?.role === 'client';

    React.useEffect(() => {
        if (isClient) { setOnboardingChecked(true); return; }
        const checkOnboarding = async () => {
            try {
                const { getOnboardingStatus } = await import('./lib/api');
                const status = await getOnboardingStatus();
                setNeedsSetup(!status.completed);
            } catch {
                setNeedsSetup(false);
            } finally {
                setOnboardingChecked(true);
            }
        };
        checkOnboarding();
    }, [isClient]);

    if (user?.must_change_password) return <Navigate to="/change-password" replace />;
    if (isClient) return <Navigate to="/dashboard" replace />;

    if (loading || !onboardingChecked) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    const hasPlan = subscription && (subscription.status === 'active' || subscription.status === 'trial');
    if (!hasPlan) return <Navigate to="/start-trial" replace />;
    if (needsSetup) return <Navigate to="/setup" replace />;
    return <Navigate to="/dashboard" replace />;
};

/** Requires user to have role admin. Redirects to /dashboard otherwise. Use inside PrivateRoute. */
const RequireAdmin = ({ children }) => {
    const { user } = useAuth();
    if (!user || user.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

/** Requires an active or trial subscription. Redirects to /start-trial to choose a plan. Client users bypass this check. Use inside PrivateRoute. */
const RequirePlan = ({ children }) => {
    const { user } = useAuth();
    const { subscription, loading } = useSubscription();

    // Force password change before accessing any content
    if (user?.must_change_password) return <Navigate to="/change-password" replace />;

    // Client users don't manage subscriptions — their MSP handles that
    if (user?.role === 'client') return children;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    const hasPlan = subscription && (subscription.status === 'active' || subscription.status === 'trial');
    if (!hasPlan) {
        return <Navigate to="/start-trial" replace />;
    }
    return children;
};

const AppRoutes = () => {
    return (
        <Routes>
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            <Route path="/register" element={
                <PublicRoute>
                    <RegisterPage />
                </PublicRoute>
            } />
            <Route path="/change-password" element={
                <PrivateRoute>
                    <ChangePasswordPage />
                </PrivateRoute>
            } />
            <Route path="/start-trial" element={
                <PrivateRoute>
                    <StartTrialPage />
                </PrivateRoute>
            } />
            <Route path="/setup" element={
                <PrivateRoute>
                    <SetupWizardPage />
                </PrivateRoute>
            } />
            <Route path="/dashboard" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <DashboardPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/devices" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <DevicesPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/alerts" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <AlertsPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/users" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <UsersPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/people" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <PeoplePage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/threats" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <ThreatDetectionPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/autofix" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <AutoFixPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/compliance" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <CompliancePage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/network" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <NetworkActivityPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/health" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <HealthPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/settings" element={
                <PrivateRoute>
                    <RequirePlan>
                        <Layout>
                            <SettingsPage />
                        </Layout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/msp" element={
                <PrivateRoute>
                    <RequirePlan>
                        <MSPLayout>
                            <MSPDashboardPage />
                        </MSPLayout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/msp/clients" element={
                <PrivateRoute>
                    <RequirePlan>
                        <MSPLayout>
                            <MSPClientsPage />
                        </MSPLayout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/msp/clients/:clientId" element={
                <PrivateRoute>
                    <RequirePlan>
                        <MSPLayout>
                            <MSPClientDetailPage />
                        </MSPLayout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/msp/settings" element={
                <PrivateRoute>
                    <RequirePlan>
                        <MSPLayout>
                            <MSPSettingsPage />
                        </MSPLayout>
                    </RequirePlan>
                </PrivateRoute>
            } />
            <Route path="/admin" element={
                <PrivateRoute>
                    <RequireAdmin>
                        <Layout>
                            <AdminPage />
                        </Layout>
                    </RequireAdmin>
                </PrivateRoute>
            } />
            <Route path="/" element={
                <PrivateRoute>
                    <DefaultRedirect />
                </PrivateRoute>
            } />
            <Route path="*" element={
                <PrivateRoute>
                    <DefaultRedirect />
                </PrivateRoute>
            } />
        </Routes>
    );
};

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster position="top-right" />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
